/**
 * Facilities Slice - Global facility state management
 *
 * Provides centralized facility data for the entire application.
 * Facilities are loaded once on app initialization and cached in Redux.
 * All pages (Dashboard, Directory, etc.) use this shared facility state.
 */

import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';

import { readFacilityScope, type FacilityScopeMode } from '../utils/facilityScope';

export interface Facility {
  id: number;
  name: string;
  code?: string | null;
  offset?: string; // Timezone offset (e.g., "-0600")
  address_line_1?: string;
  address_city?: string;
  address_state?: string;
}

interface FacilitiesState {
  facilities: Facility[];
  loading: boolean;
  error: string | null;
  selectedFacilityId: number | null;
  /** 'all' when the user chose to span every facility at login (issue #243). */
  scopeMode: FacilityScopeMode;
}

/**
 * Read facility ID from Django-rendered window globals (set in react_shell.html).
 * This makes the facility available on the very first render — before
 * the async fetchFacilities thunk resolves — matching the AngularJS behaviour
 * where the facility was injected synchronously via Django template tags.
 */
function getInitialFacilityId(): number | null {
  // Primary: the scope rendered by get_facility_scope, which already normalises the session
  // facility. Falls through to the older globals when a page rendered no scope.
  const scopeFacilityId = readFacilityScope().facilityId;
  if (scopeFacilityId !== null) return scopeFacilityId;

  // window.facility (set by extend_context_for_facility)
  const facility = (window as any)?.facility;
  if (facility?.id) {
    const id = typeof facility.id === 'number' ? facility.id : parseInt(facility.id, 10);
    if (!isNaN(id)) return id;
  }

  // Fallback: tenantContext.facilityId (string, rendered by Django template)
  const tenantContext = (window as any)?.tenantContext;
  if (tenantContext?.facilityId) {
    const id = parseInt(tenantContext.facilityId, 10);
    if (!isNaN(id)) return id;
  }

  return null;
}

const initialState: FacilitiesState = {
  facilities: [],
  loading: false,
  error: null,
  selectedFacilityId: getInitialFacilityId(),
  scopeMode: readFacilityScope().mode,
};

/**
 * Fetch facilities from Django API
 * Uses /dashboard/get_dashboard_info endpoint which returns JSON with facilities array
 */
export const fetchFacilities = createAsyncThunk(
  'facilities/fetch',
  async () => {
    const response = await fetch('/dashboard/get_dashboard_info', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': getCSRFToken(),
      },
    });

    // Handle session expiration - redirect to login
    if (response.status === 401 || response.status === 403) {
      const currentPath = window.location.pathname + window.location.search;
      window.location.href = `/login?next=${encodeURIComponent(currentPath)}`;
      return [];
    }

    if (!response.ok) {
      throw new Error(`Failed to fetch facilities: ${response.status}`);
    }

    const data = await response.json();
    return data.facilities || [];
  }
);

/**
 * Get CSRF token from window context or cookie
 */
function getCSRFToken(): string {
  const globalContext = (window as any).globalContext;
  if (globalContext?.authContext?.csrf_token) {
    return globalContext.authContext.csrf_token;
  }

  // Fallback: get from cookie
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'csrftoken') {
      return decodeURIComponent(value);
    }
  }

  throw new Error('CSRF token not found');
}

const facilitiesSlice = createSlice({
  name: 'facilities',
  initialState,
  reducers: {
    // Set selected facility from tenant context or user selection
    setSelectedFacility: (state, action: PayloadAction<number | null>) => {
      state.selectedFacilityId = action.payload;
    },

    // Clear facilities (e.g., on logout)
    clearFacilities: (state) => {
      state.facilities = [];
      state.selectedFacilityId = null;
      state.scopeMode = 'single';
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFacilities.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFacilities.fulfilled, (state, action) => {
        state.loading = false;
        state.facilities = action.payload;

        // Auto-select facility from tenant context if not already set
        if (state.selectedFacilityId === null) {
          const tenantContext = (window as any)?.tenantContext;

          if (tenantContext?.facilityId) {
            const facilityId = parseInt(tenantContext.facilityId);
            if (!isNaN(facilityId)) {
              state.selectedFacilityId = facilityId;
            }
          }
        }
      })
      .addCase(fetchFacilities.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch facilities';
      });
  },
});

export const { setSelectedFacility, clearFacilities } = facilitiesSlice.actions;
export default facilitiesSlice.reducer;
